import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import PageHeader from "../components/layout/PageHeader";
import { 
  Trophy, 
  Flame, 
  Star, 
  Target, 
  BookOpen, 
  Languages,
  BookMarked,
  ChevronRight,
  Lock,
  Medal,
  Brain
} from "lucide-react";

const Quiz = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [progressRes, leaderboardRes] = await Promise.all([
        apiClient.get("/quiz/progress"),
        apiClient.get("/quiz/leaderboard"),
      ]);
      setProgress(progressRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const quizCategories = [
    { 
      id: "surah-names", 
      title: isEnglish ? "Surah Names" : "Nama Surah", 
      description: isEnglish ? "Guess the surah name from a verse" : "Tebak nama surah dari ayat",
      icon: BookOpen,
      color: "bg-blue-500"
    },
    { 
      id: "ayah-completion", 
      title: isEnglish ? "Complete the Verse" : "Lanjutkan Ayat", 
      description: isEnglish ? "Complete the missing part of a verse" : "Lengkapi ayat yang terpotong",
      icon: BookMarked,
      color: "bg-purple-500"
    },
    { 
      id: "memorization", 
      title: isEnglish ? "Memorized Surahs" : "Hafalan Selesai", 
      description: isEnglish ? "Quiz from surahs you completed" : "Quiz dari surah yang sudah selesai dihafal",
      icon: Brain,
      color: "bg-emerald-600"
    },
    { 
      id: "translation", 
      title: isEnglish ? "Translation" : "Terjemahan", 
      description: isEnglish ? "Match verses with their translations" : "Cocokkan ayat dengan terjemahan",
      icon: Languages,
      color: "bg-green-500"
    },
    { 
      id: "random", 
      title: isEnglish ? "Mixed" : "Campuran", 
      description: isEnglish ? "All question types" : "Semua jenis soal",
      icon: Target,
      color: "bg-islamic-gold"
    },
  ];

  const achievements = [
    { id: "first_quiz", title: isEnglish ? "First Quiz" : "Quiz Pertama", icon: "🎯", unlocked: progress?.achievements?.includes("first_quiz") },
    { id: "quiz_master", title: "Quiz Master", icon: "👑", description: isEnglish ? "10 quizzes completed" : "10 quiz selesai", unlocked: progress?.achievements?.includes("quiz_master") },
    { id: "week_streak", title: isEnglish ? "One Week Streak" : "Streak Seminggu", icon: "🔥", description: isEnglish ? "7 days in a row" : "7 hari berturut", unlocked: progress?.achievements?.includes("week_streak") },
    { id: "perfect_score", title: isEnglish ? "Perfect Score" : "Nilai Sempurna", icon: "⭐", description: isEnglish ? "100% correct" : "100% benar", unlocked: progress?.achievements?.includes("perfect_score") },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5" data-testid="quiz-page">
      <PageHeader title="Quiz" />

      <div className="page-container space-y-6">
        {/* Stats Header */}
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <Card className="border-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <CardContent className="p-2 md:p-5 text-center space-y-1 md:space-y-2">
              <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-islamic-gold/20 to-islamic-gold/10 flex items-center justify-center mx-auto">
                <Trophy className="w-4 h-4 md:w-6 md:h-6 text-islamic-gold" />
              </div>
              <p className="text-xl md:text-3xl font-bold text-foreground">{progress?.total_points || 0}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground font-medium">{isEnglish ? "Points" : "Poin"}</p>
            </CardContent>
          </Card>
          <Card className="border-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <CardContent className="p-2 md:p-5 text-center space-y-1 md:space-y-2">
              <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-500/10 flex items-center justify-center mx-auto">
                <Flame className="w-4 h-4 md:w-6 md:h-6 text-orange-500" />
              </div>
              <p className="text-xl md:text-3xl font-bold text-foreground">{progress?.streak_days || 0}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground font-medium">{isEnglish ? "Day Streak" : "Streak Hari"}</p>
            </CardContent>
          </Card>
          <Card className="border-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <CardContent className="p-2 md:p-5 text-center space-y-1 md:space-y-2">
              <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mx-auto">
                <Star className="w-4 h-4 md:w-6 md:h-6 text-primary" />
              </div>
              <p className="text-xl md:text-3xl font-bold text-foreground">{progress?.quizzes_completed || 0}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground font-medium">{isEnglish ? "Completed" : "Selesai"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Challenge */}
        <Card className="islamic-gradient text-white border-0 shadow-2xl overflow-hidden relative group cursor-pointer hover:scale-[1.02] transition-all duration-300" onClick={() => navigate("/quiz/play?category=random")} data-testid="daily-challenge-card">
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <CardContent className="p-3 md:p-6 relative">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-2 md:space-y-3 flex-1">
                <Badge className="bg-white/20 text-white border-0 hover:bg-white/30 transition-colors text-[10px] md:text-xs">
                  {isEnglish ? "Daily Challenge" : "Tantangan Harian"}
                </Badge>
                <h3 className="text-lg md:text-2xl font-bold">{isEnglish ? "Today's Quiz" : "Quiz Hari Ini"}</h3>
                <p className="text-white/90 text-xs md:text-sm font-medium">{isEnglish ? "10 questions • +50 points" : "10 soal • +50 poin"}</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="shadow-lg hover:shadow-xl transition-all text-xs md:text-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/quiz/play?category=random");
                }}
                data-testid="start-quiz-btn"
              >
                {isEnglish ? "Start" : "Mulai"} <ChevronRight className="w-3 h-3 md:w-4 md:h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quiz Categories */}
        <section className="space-y-3 md:space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="section-title text-lg md:text-xl">{isEnglish ? "Quiz Categories" : "Kategori Quiz"}</h2>
          </div>
          <div className="grid grid-cols-2 gap-2 md:gap-4">
            {quizCategories.map((category) => (
              <Card
                key={category.id}
                className="cursor-pointer border-2 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group overflow-hidden pointer-events-auto"
                onClick={() => navigate(`/quiz/play?category=${category.id}`)}
                data-testid={`quiz-category-${category.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                <CardContent className="p-3 md:p-5 relative pointer-events-none">
                  <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl ${category.color} text-white flex items-center justify-center mb-2 md:mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <category.icon className="w-5 h-5 md:w-7 md:h-7" />
                  </div>
                  <h3 className="font-bold text-sm md:text-lg mb-1">{category.title}</h3>
                  <p className="text-[10px] md:text-xs text-muted-foreground leading-relaxed line-clamp-2">{category.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Achievements */}
        <section className="space-y-3 md:space-y-4">
          <h2 className="section-title text-lg md:text-xl">{isEnglish ? "Achievements" : "Pencapaian"}</h2>
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-3 md:pb-4 scrollbar-hide">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className={`flex-shrink-0 w-20 md:w-24 text-center space-y-1.5 md:space-y-2 transition-all duration-300 hover:scale-105 ${!achievement.unlocked ? "opacity-40" : ""}`}
              >
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg md:text-2xl mx-auto ${achievement.unlocked ? 'quiz-glow' : ''} ${
                  achievement.unlocked ? "achievement-unlocked" : "achievement-locked"
                }`}>
                  {achievement.unlocked ? achievement.icon : <Lock className="w-5 h-5 md:w-7 md:h-7" />}
                </div>
                <p className="text-[10px] md:text-xs font-semibold truncate px-1">{achievement.title}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Leaderboard */}
        <section className="space-y-3 md:space-y-4">
          <h2 className="section-title text-lg md:text-xl">{isEnglish ? "Leaderboard" : "Papan Peringkat"}</h2>
          {loading ? (
            <div className="space-y-2 md:space-y-3">
              {[1, 2, 3].map(i => (
                <Card key={i} className="border-2">
                  <CardContent className="p-3 md:p-4">
                    <Skeleton className="h-12 md:h-16 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 shadow-lg">
              <CardContent className="p-0 divide-y divide-border">
                {leaderboard.length === 0 ? (
                  <div className="p-6 md:p-10 text-center text-muted-foreground">
                    <Trophy className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-50" />
                    <p className="text-sm md:text-base">{isEnglish ? "No data yet" : "Belum ada data"}</p>
                  </div>
                ) : (
                  leaderboard.slice(0, 5).map((entry, index) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-2 md:gap-4 p-3 md:p-4 transition-colors ${
                        entry.user_id === user?.user_id ? "bg-primary/10" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-bold shadow-md flex-shrink-0 ${
                        index === 0 ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white" :
                        index === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400 text-white" :
                        index === 2 ? "bg-gradient-to-br from-orange-400 to-orange-500 text-white" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : index + 1}
                      </div>
                      <Avatar className="w-9 h-9 md:w-12 md:h-12 border-2 border-border shadow-md flex-shrink-0">
                        <AvatarImage src={entry.picture} />
                        <AvatarFallback className="font-bold text-xs md:text-base">{entry.name?.charAt(0) || "?"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-xs md:text-sm truncate">{entry.name || "User"}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-sm md:text-lg text-primary">{entry.total_points}</p>
                        <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "points" : "poin"}</p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};

export default Quiz;
